import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {QueryRef} from 'apollo-angular';

import { LazyLoadEvent, MenuItem } from 'primeng/api';
import { Table } from 'primeng/table';
import { Subject, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { BaseReportTypeFragment, ReportTypesListGQL, ReportTypesListQuery, ReportTypesListQueryVariables, ZoneDir } from '../../../generated/graphql.generated';
import { FeedbackService } from '../../core/public-api';
import { DASHBOARDS, DashboardEntry, getDashboardPermissions, isDashboardHidden, isDashboardVisibleInZone, isDashboardVisibleInZoneType } from '../../dashboardv2/dashboards-list';
import { pagination } from '../../global.constants';
import { BrandingService } from '../../services/branding.service';
import { DetailsHelperService } from '../../services/details-helper.service';
import { PermissionService } from '../../services/permission.service';
import { initQueryHelper, WatchQueryHelper } from '../../utilities/watchQueryHelper';

const newReportTypeMessage = `\
I'd like to request a new report type:

What would you like the report called?


What data do you want this report to return? What format?


How would you like the data summarized?


Who will be using this report?


Do you have an example report from another application?

`;

@Component({
  selector: 'app-report-types',
  templateUrl: './report-types.component.html',
  styleUrls: ['./report-types.component.scss']
})
export class ReportTypesComponent implements OnInit, OnDestroy {

  @ViewChild('table') table: Table;

  @Input() pagination = pagination;

  reportTypes: ReportTypesListQuery['reportTypes']['reportTypes'];
  reportTypesQH: WatchQueryHelper = {};

  filterUpdated = new Subject<void>();

  showCreateReportTypeDialog = false;

  /** PrimeNG breadcrumb home item — always links to the app root. */
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/home' };

  /**
   * Requirement: Restored PrimeNG p-breadcrumb — rebuilt whenever the active
   * folder path changes so it always reflects the current location.
   * Each segment except the last is a clickable routerLink.
   */
  breadcrumb: MenuItem[] = [
    { label: 'Reports', routerLink: '/reports' },
  ];

  /** Requirement: Show the legacy static reports table only when the user navigates
   * to the virtual /Static folder inside DynamicReportsComponent. */
  isStaticPath = false;

  /** Whether the user is at the reports root (not inside a subfolder). */
  isRootPath = true;

  /** Dashboards shown in the card above the reports search. Only shown at root level. */
  visibleDashboards: DashboardEntry[] = [];

  private subsink = new SubSink();
  private queryRef: QueryRef<ReportTypesListQuery, ReportTypesListQueryVariables>;

  constructor(
    private reportTypesService: ReportTypesListGQL,
    private detailsHelper: DetailsHelperService,
    private feedbackService: FeedbackService,
    private branding: BrandingService,
    private permissionService: PermissionService,
  ) { }

  ngOnInit(): void {
    this.reportTypesQH = initQueryHelper({
      skip: 0,
      limit: 50,
      search: '',
      sort: ['name:ASC'],
    });
    this.fetch();
    this.subsink.sink = this.filterUpdated.pipe(
      debounceTime(800)
    ).subscribe(() => {
      // ensure we set skip to zero when we apply a new filter
      this.setFirstPage();
      this.fetch();
    });
    // Requirement: recompute on zone change AND on permission change. The
    // branding query delivers the new zone's permissions *after* currentZone
    // emits, so a zone-only subscription builds the list from the previous
    // zone's permissions and never revisits it. Both sources replay their
    // current value on subscribe, so this also covers the initial render.
    this.subsink.sink = merge(
      this.branding.currentZone(),
      this.permissionService.watch(),
    ).subscribe(() => this.recomputeDashboards());
  }

  private recomputeDashboards(): void {
    const zone = this.branding.currentZone().value;
    const zoneId = zone?.id;
    const zoneType = zone?.type;
    this.visibleDashboards = DASHBOARDS.filter((d) => {
      if (isDashboardHidden(d)) { return false; }
      const perms = getDashboardPermissions(d);
      if (perms.length && !perms.every((p) => this.permissionService.checkPermission(p))) { return false; }
      if (!isDashboardVisibleInZone(d, zoneId)) { return false; }
      if (!isDashboardVisibleInZoneType(d, zoneType)) { return false; }
      return true;
    });
  }

  ngOnDestroy(): void {
    delete this.queryRef;
    this.subsink.unsubscribe();
  }

  /**
   * Called by DynamicReportsComponent whenever the active folder path changes.
   * Rebuilds the PrimeNG breadcrumb model to match the new path.
   */
  onPathChanged(path: string) {
    this.isStaticPath = path === '/Static';
    this.isRootPath = path === '/';
    this.breadcrumb = this.buildBreadcrumb(path);
  }

  /**
   * Requirement: Shared search — called when DynamicReportsComponent emits
   * searchChanged. When the user is browsing /Static, propagate the search
   * term to the static reports query so both tables share one search bar.
   */
  onSearchChanged(search: string) {
    if (!this.isStaticPath) { return; }
    this.reportTypesQH.search = search;
    this.setFirstPage();
    this.fetch();
  }

  /**
   * Converts a folder path string like "/Finance/Q1" into a PrimeNG MenuItem[]
   * suitable for the <p-breadcrumb> component.
   * The last segment is rendered as plain text (no routerLink) by PrimeNG when
   * there is no `url` or `routerLink` property — so we leave it without one.
   */
  private buildBreadcrumb(path: string): MenuItem[] {
    // Root always shows just "Reports"
    if (!path || path === '/') {
      return [{ label: 'Reports', routerLink: '/reports' }];
    }

    const items: MenuItem[] = [
      { label: 'Reports', routerLink: '/reports' },
    ];

    const segments = path.split('/').filter(Boolean);
    let built = '';

    segments.forEach((seg, idx) => {
      built += '/' + seg;
      const isLast = idx === segments.length - 1;
      // Requirement: All breadcrumb segments are links so the user can navigate
      // back up the folder tree by clicking any crumb.
      const routerParts = built.split('/').filter(Boolean);
      items.push({
        label: seg,
        routerLink: ['/reports', ...routerParts],
        // PrimeNG will not style the last item as a link; pass styleClass to keep it consistent
        styleClass: isLast ? 'breadcrumb-current' : '',
      });
    });

    return items;
  }

  getVariables() {
    return {
      filter: {
        zoneDir: ZoneDir.Any,
        getDeleted: false,
        search: this.reportTypesQH.search,
      },
      skip: this.reportTypesQH.skip,
      limit: this.reportTypesQH.limit,
      sort: this.reportTypesQH.sort.join(';'),
    } as ReportTypesListQueryVariables;

  }

  clearSearch() {
    this.reportTypesQH.search = '';
    this.fetch();
  }

  fetch() {
    if (this.queryRef) {
      this.queryRef.refetch(this.getVariables());
      return;
    }

    this.queryRef = this.reportTypesService.watch(this.getVariables(), {
      fetchPolicy: 'cache-and-network',
    });
    this.subsink.sink = this.queryRef.valueChanges.subscribe((res) => {
      this.reportTypesQH.loading = res.loading;
      this.reportTypesQH.total = res.data?.reportTypes?.total;
      this.reportTypes = res.data?.reportTypes?.reportTypes;
    });
  }

  onLazyLoad(ev: LazyLoadEvent) {
    this.reportTypesQH.limit = ev.rows;
    this.reportTypesQH.skip = ev.first;
    this.fetch();
  }

  select(rt: BaseReportTypeFragment) {
    this.detailsHelper.detailsItem.next({ type: 'reportType', item: rt });
  }

  setFirstPage() {
    if (this.table) {
      this.table.first = 0;
    }
    this.reportTypesQH.skip = 0;
  }

  openFeedback() {
    this.feedbackService.openFeedbackDialog({
      feedbackType: 'feedback',
      message: newReportTypeMessage,
    });
  }

}
